import { TheWestSection } from '@news-mono/common'
import {
    BreachScreenContext,
    BreachScreenType,
    click_origin_breach_screen,
    DataLayerEventName,
    SwgMetaInfo,
    useFeature,
    useSubscribeWithGoogle,
} from '@news-mono/web-common'
import { TheWestRegionalSections } from '@west-australian-newspapers/publication-types'
import React, {
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
} from 'react'
import { getCartPayload } from '../../../../'
import { useSection, useSectionMetaInfo } from '../../../../__helpers'
import { IconStar } from '../../../../icons/IconStar/IconStar'
import { useAuthActions } from '../../../../user-registration/authentication/AuthenticationProvider'
import { createLinkClickEvent } from '../../createLinkClickHandler'
import {
    BreachWallContentType,
    SubscriptionPackageType,
    SubscriptionPriceObject,
} from '../../helpers/BreachWallTypes'
import type {
    BreachOptions,
    PackageOptions,
    SubscriptionPackageProps,
} from '../BreachWallContent/BreachWallContent'
import {
    RadioSubscriptionPriceDisplay,
    SingleSubscriptionPriceDisplay,
} from '../SubscriptionPriceDisplay/SubscriptionPriceDisplay'
import { ComputedPackageImage } from './ComputedPackageImage'
import {
    Ribbon,
    StyledPackageImage,
    StyledSubscriptionButton2024,
    StyledSubscriptionCancellationMessage,
    StyledSubscriptionPackage2022,
    StyledSubscriptionPackageActions,
    StyledSubscriptionPackageHeading2022,
    StyledSwgButton2024,
} from './SubscriptionPackage.styled'
import { SubscriptionPackageBenefit } from './SubscriptionPackageBenefit'

//Footy pack
const footyPackEverydayDigital = require('./images/footypack2023/afl_everyday_digital.webp')

//EOFY
const eofyEverydayDigital = require('./images/eofy/eofy_everyday_digital.webp')
const eofyFriMonPrintDigital = require('./images/eofy/eofy_everyday_digital_and_weekend_papers.webp')

const everyDayDigitalPromo = require('./images/updated2022/everyday_digital_190_year.webp')

const everyDayDigitalJollyRodgers = require('./images/updated2022/redesign/everyday_digital_june23.webp')
const everyDayDigitalPaperJollyRodgers = require('./images/updated2022/redesign/everyday_digital_and_weekend_papers_june23.webp')

// default breach screen product images
const everyDayDigitalDefault = require('./images/freedelivery2025/everyday_digital_free_delivery2025.webp')
const everyDayDigitalPaperDefault = require('./images/freedelivery2025/everyday_digital+weekend_papers_free_delivery2025.webp')

export const SubscriptionPackage: React.FC<SubscriptionPackageProps> = ({
    breachDeal,
    breachCreativeName,
    breachType = 'default',
    onEvent,
    masthead = 'The West Australian',
    packageInfo,
    packagePrices: providedPackagePrices,
    selectedRadioPackage: providedSelectedRadioPackage,
    isRegionalOverride,
    desiredRedirect,
    isSwgPage = false,
}) => {
    const {
        heading,
        ribbonTitle,
        ribbonOptions,
        packageTheme = 'default',
        packageType,
        buttonStyle,
        benefitOverride,
        type: subscriptionPackageType,
    } = packageInfo

    const isRadio = packageInfo.type === 'radio-list-subscriptions'
    const packagePrices = useMemo(
        () =>
            providedPackagePrices
                ? providedPackagePrices
                : isRadio
                ? packageInfo.prices
                : [packageInfo],
        [isRadio, packageInfo, providedPackagePrices],
    )
    const defaultPackage = isRadio ? packageInfo.prices[0] : [packageInfo][0]
    const [selectedPrice, setSelectedPrice] = useState(defaultPackage)
    const [storedProvidedPrice, setStoredProvidedPrice] = useState<
        SubscriptionPriceObject | undefined
    >()

    // If a selected radio package index is provided, use it to set the selected price
    React.useEffect(() => {
        if (providedSelectedRadioPackage !== undefined) {
            const newSelectedPrice = packagePrices[providedSelectedRadioPackage]

            if (
                !storedProvidedPrice ||
                (newSelectedPrice && storedProvidedPrice !== newSelectedPrice)
            ) {
                setSelectedPrice(newSelectedPrice)
                setStoredProvidedPrice(newSelectedPrice)
            }
        }
    }, [
        providedSelectedRadioPackage,
        selectedPrice,
        packagePrices,
        storedProvidedPrice,
    ])

    const { cancellationMessage, googleSKU, packagePath, offerCode } =
        selectedPrice

    const { onSubscribeClick } = useAuthActions()

    const subscribeWithGoogleEnabled = useFeature('subscribe-with-google')
    const googleRrmeEnabled = useFeature('google-rrme-full-override')
    const section = useSection<TheWestSection>()

    const breachName = section === 'genwest' ? 'default' : section

    const isBlackFriday = breachType === 'black-friday'
    const benefits = getBenefits(packageType, benefitOverride)

    const onPackageChange = useCallback(
        (pricePoint: SubscriptionPriceObject) => {
            const current = selectedPrice

            setSelectedPrice(pricePoint)

            onEvent({
                type: DataLayerEventName.billingPeriodChange,
                originator: 'SubscriptionPackage',
                payload: {
                    from: {
                        weeklyPrice: current.weeklyPrice,
                        rateCode: current.rateCode,
                        offerCode: current.offerCode,
                    },
                    to: {
                        weeklyPrice: pricePoint.weeklyPrice,
                        rateCode: pricePoint.rateCode,
                        offerCode: pricePoint.offerCode,
                    },
                },
            })
        },
        [onEvent, selectedPrice],
    )

    // Matches swg-breach-albany-advertiser etc
    const subscribeWithGoogleBreachInterfaceEnabled = useFeature(
        `swg-breach-${breachName}`,
    )

    const buttonRef = useRef<HTMLButtonElement>(null)

    const showSwG =
        (subscribeWithGoogleEnabled || googleRrmeEnabled) &&
        subscribeWithGoogleBreachInterfaceEnabled &&
        googleSKU

    const sectionMeta = useSectionMetaInfo()

    const breachScreenContext = useContext(BreachScreenContext)
    const breachContext = breachScreenContext.context
    const breachScreenType: BreachScreenType = 'Premium - Inline'

    let swgMetaInfo: SwgMetaInfo | undefined = undefined
    if (desiredRedirect) {
        swgMetaInfo = { desiredRedirect }
    }

    useSubscribeWithGoogle(
        buttonRef.current as HTMLButtonElement,
        googleSKU || 'everyday_digital_4weeks',
        sectionMeta,
        breachContext,
        swgMetaInfo,
        googleRrmeEnabled,
    )

    const addToCartPayload = getCartPayload(
        { price: selectedPrice, package: packageInfo },
        masthead,
    )
    const isDigital = packageType === 'digital'
    const isDigitalPrint = packageType === 'digitalprint'

    const isRegional = section in TheWestRegionalSections

    const packageOptions: PackageOptions = {
        theme: packageTheme,
        type: packageType,
    }
    const breachOptions: BreachOptions = {
        useBreachDealStyle: isRegionalOverride,
        isRegional: isRegional,
        breachType,
    }

    const benefitColor = isBlackFriday ? '#043de2' : undefined

    // Get package images & scales based on the breach style & deal being used.
    const digitalImage = getDigitalImage(breachDeal, breachType)
    const digitalPaperImage = getDigitalPrintImage(breachType)
    const digitalScale = digitalImage
        ? getCustomImageScale(digitalImage)
        : undefined
    const digitalPaperScale = getCustomImageScale(digitalPaperImage)

    const displayPackageImages =
        breachType === 'afl-footy-lives-here-breach-screen' ||
        breachType === '50-off-campaign'

    const renderRibbonTitle = (title: string) => {
        if (!title) return null
        if (breachType !== 'black-friday') return title
        const words = title.split(' ')
        return words.map((word, idx) => (
            <React.Fragment key={idx}>
                {word}
                {idx < words.length - 1 && <br />}
            </React.Fragment>
        ))
    }

    return (
        <StyledSubscriptionPackage2022
            packageOptions={packageOptions}
            breachOptions={breachOptions}
        >
            {/* General Package Ribbon */}
            {ribbonTitle && (
                <Ribbon
                    packageOptions={{
                        ...packageOptions,
                        theme: packageTheme,
                    }}
                    breachOptions={breachOptions}
                    className={ribbonOptions ? ribbonOptions : 'newDesignTag'}
                >
                    <span>
                        {ribbonOptions != 'bluePill' && <IconStar />}
                        {renderRibbonTitle(ribbonTitle)}
                    </span>
                </Ribbon>
            )}

            {/* Package Image */}
            {isDigital &&
                displayPackageImages &&
                (digitalImage ? (
                    <StyledPackageImage
                        src={digitalImage}
                        customScale={digitalScale}
                        packageType={packageType}
                        alt=""
                        role="presentation"
                        padding={16}
                    />
                ) : (
                    <ComputedPackageImage section={section} />
                ))}
            {isDigitalPrint && displayPackageImages && (
                <StyledPackageImage
                    src={digitalPaperImage}
                    customScale={digitalPaperScale}
                    packageType={packageType}
                    alt=""
                    role="presentation"
                />
            )}

            {/* Package Heading */}
            <StyledSubscriptionPackageHeading2022
                packageOptions={packageOptions}
                breachOptions={breachOptions}
            >
                {heading}
            </StyledSubscriptionPackageHeading2022>

            {/* Display price points */}
            {subscriptionPackageType === 'single-subscription' ? (
                <SingleSubscriptionPriceDisplay
                    subscriptionPackage={packageInfo}
                    packageOptions={packageOptions}
                    breachOptions={breachOptions}
                />
            ) : (
                <RadioSubscriptionPriceDisplay
                    subscriptionPackage={packageInfo}
                    packageOptions={packageOptions}
                    breachOptions={breachOptions}
                    selectedPricePoint={selectedPrice}
                    packagePrices={packagePrices}
                    setSelectedPricePoint={onPackageChange}
                />
            )}

            <StyledSubscriptionPackageActions
                paddingBot={20}
                isSwgPage={isSwgPage}
            >
                {!isSwgPage && (
                    <StyledSubscriptionButton2024
                        packageOptions={packageOptions}
                        breachOptions={breachOptions}
                        buttonStyle={buttonStyle}
                        buttonWidth="wide"
                        type="button"
                        title="Subscribe Button"
                        aria-label="Subscribe button"
                        onClick={() => {
                            createLinkClickEvent(
                                breachCreativeName,
                                breachDeal,
                                onEvent,
                                breachScreenType,
                                1,
                                addToCartPayload,
                            )()
                            onSubscribeClick({
                                packagePath,
                                offerCode,
                                callToAction: click_origin_breach_screen,
                                breachScreenType: breachScreenType,
                            })
                        }}
                    >
                        Subscribe
                    </StyledSubscriptionButton2024>
                )}

                {isDigital && showSwG && (
                    <StyledSwgButton2024
                        ref={buttonRef}
                        type="button"
                        aria-label="Subscribe with google button"
                    />
                )}
            </StyledSubscriptionPackageActions>

            {benefits.map((benefit) => (
                <SubscriptionPackageBenefit
                    benefit={benefit}
                    key={benefit.name}
                    color={benefitColor}
                    packageOptions={packageOptions}
                    breachOptions={breachOptions}
                />
            ))}

            <StyledSubscriptionPackageActions isLast>
                <StyledSubscriptionCancellationMessage
                    packageOptions={packageOptions}
                    breachOptions={breachOptions}
                >
                    {cancellationMessage}
                </StyledSubscriptionCancellationMessage>
            </StyledSubscriptionPackageActions>
        </StyledSubscriptionPackage2022>
    )
}

export const PACKAGE_BENEFIT_NAMES = [
    'Access to all digital content',
    'Regional online news',
    'The West app',
    'Daily digital edition newspaper',
    'Subscriber rewards',
    'Weekend papers delivered',
] as const

export type SubscriptionPackageBenefitValue = {
    name: typeof PACKAGE_BENEFIT_NAMES[number]
    isAvailable: boolean
}

export const getBenefits = (
    packageType?: SubscriptionPackageType,
    customBenefit?: string[],
) =>
    customBenefit
        ? customBenefit
              .map((benefit) => ({
                  name: benefit,
                  isAvailable:
                      benefit === 'Weekend papers delivered'
                          ? packageType === 'digitalprint'
                          : true,
              }))
              .sort((a, b) =>
                  a.isAvailable === b.isAvailable
                      ? 0
                      : b.isAvailable > a.isAvailable
                      ? 1
                      : -1,
              )
        : PACKAGE_BENEFIT_NAMES.map((benefit) => ({
              name: benefit,
              isAvailable:
                  benefit === 'Weekend papers delivered'
                      ? packageType === 'digitalprint'
                      : true,
          })).sort((a, b) =>
              a.isAvailable === b.isAvailable
                  ? 0
                  : b.isAvailable > a.isAvailable
                  ? 1
                  : -1,
          )

function getDigitalImage(
    breachDeal: string,
    breachType?: BreachWallContentType,
): string | undefined {
    // check against the breach styles for unique images
    switch (breachType) {
        case 'vanishing-cousins':
        case 'black-friday':
            return everyDayDigitalJollyRodgers
        case 'afl-footy-lives-here-breach-screen':
            return footyPackEverydayDigital
        case '50-off-campaign':
            return eofyEverydayDigital
    }
    // now check against deal/sub-sources
    switch (breachDeal) {
        case '190BD23':
            return everyDayDigitalPromo
    }

    if (breachType !== undefined) {
        return everyDayDigitalPromo
    }

    return everyDayDigitalDefault
}
function getDigitalPrintImage(breachType: BreachWallContentType): string {
    switch (breachType) {
        case 'black-friday':
            return everyDayDigitalPaperJollyRodgers
        case '50-off-campaign':
            return eofyFriMonPrintDigital
    }

    return everyDayDigitalPaperDefault
}
function getCustomImageScale(image: string): number | undefined {
    switch (image) {
        case everyDayDigitalPaperJollyRodgers:
            return 1.5
        case everyDayDigitalJollyRodgers:
            return 1.4
    }
}

SubscriptionPackage.displayName = 'SubscriptionPackage'
export default SubscriptionPackage
